import React, { useEffect, useMemo, useState } from "react";
import Header from "../../layout/Header";
import { getColumns } from "./listColumn";
import { getDisplayDate } from "../../../utils/helper";
import Paginated from "./Paginated";
import TemplateTable from "./TemplateTable";
import {
  getDynamicdata,
  getQRCode,
  GetQRDataByCode,
} from "../../../services/services";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const QRCodeList = () => {
  const [loader, setLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const [templates, setTemplates] = useState([]);
  const [templateList, setTemplateList] = useState([]);

  const merCode = localStorage.getItem("Id");
  const [showCustomerColumns, setShowCustomerColumns] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [templatePageSize, setTemplatePageSize] = useState(10);
  const COLUMNS = useMemo(
    () => getColumns(showCustomerColumns),
    [showCustomerColumns],
  );

  useEffect(() => {
    initTemplates();
  }, []);

  const initTemplates = async () => {
    try {
      setLoader(true);
      const tokenRes = JSON.parse(localStorage.getItem("token_gen"));
      const logsRes = await getDynamicdata(tokenRes.access_token, "", "");
      const logs = logsRes?.data || [];

      const productList = logs
        .map((val) => val?.requestData)
        .filter((req) => req?.code);

      const rows = logs
        .map((val) => ({
          templateCode: val?.requestData?.code,
          useremail: val?.requestData?.discriminatorType,
          productdescription: (val?.requestData?.productName || "")
            .replace(/\s+/g, " ")
            .trim(),
          batchno: val?.requestData?.others?.subCategory,
          quantity: val?.requestData?.quantity,
          value: val?.requestData?.value,
          datetime: val?.CreatedDate ? getDisplayDate(val?.CreatedDate) : "",
        }))
        .reverse();

      setTemplates(productList);
      setTemplateList(rows);
    } catch (err) {
      console.error("QR Logs API error:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchAllRawQR = async (templateCode) => {
    const tokenRes = JSON.parse(localStorage.getItem("token_gen"));
    const pageSize = 1000;
    let page = 1;
    let totalCount = 0;
    let allRows = [];

    do {
      const response = await getQRCode(
        tokenRes.access_token,
        merCode,
        templateCode,
        page,
        pageSize,
      );

      if (
        response?.message !== "Record fetched successfully." ||
        !response?.data?.length
      )
        break;

      allRows = allRows.concat(response.data);
      totalCount = Number(response.totalCount) || 0;
      page++;
    } while (allRows.length < totalCount);

    return allRows;
  };

  const mapQRRow = (ele) => {
    const matchedProduct = templates.find((p) => p.code === ele.templatecode);

    return {
      ...ele,
      qr: ele.qrcode ?? "",
      encryptCode: ele.code ? ele.code.toString().slice(0, -4) + "****" : "",
      productName: matchedProduct?.productName || "",
      status:
        ele.status === "Active" ? (
          "Un Scan"
        ) : (
          <span className="text-danger">Scanned</span>
        ),
      createddate: getDisplayDate(ele.createddate),
      scandate:
        ele.status?.toLowerCase() === "inactive"
          ? getDisplayDate(ele.updateddate)
          : "",
    };
  };

  const searchQRByCode = async (qrCode) => {
    if (!qrCode?.trim()) {
      setShowCustomerColumns(false);
      setFilteredData([]);
      setHasSearched(false);
      return;
    }
    setHasSearched(true);
    try {
      setLoader(true);
      const tokenRes = JSON.parse(localStorage.getItem("token_gen"));
      const response = await GetQRDataByCode(
        tokenRes.access_token,
        qrCode.trim(),
      );
      const qrData = response?.data?.qrData;
      if (!qrData) {
        setShowCustomerColumns(false);
        setFilteredData([]);
        return;
      }
      const transactions = response?.data?.qrTransaction || [];
      const row = mapQRRow({
        ...qrData,
        templatecode: qrData.templateCode,
        createddate: qrData.createdDate,
        updateddate:
          transactions[0]?.transactionProcessedDate || qrData.updatedDate,
      });
      if (transactions.length) {
        row.customerName = transactions[0]?.customer?.[0]?.name || "";
      }
      setShowCustomerColumns(transactions.length > 0);
      setFilteredData([row]);
    } catch (err) {
      console.error("QR search error:", err);
      setShowCustomerColumns(false);
      setFilteredData([]);
    } finally {
      setLoader(false);
    }
  };

  const fetchAllQRByTemplate = async (templateCode) => {
    if (!templateCode) {
      alert("Please select a product to download.");
      return [];
    }
    try {
      setLoader(true);
      const allRows = await fetchAllRawQR(templateCode);

      return allRows.map((ele) => {
        const matchedProduct = templates.find(
          (p) => p.code === ele.templatecode,
        );

        return {
          ...ele,
          qr: ele.qrcode ?? "",
          productName: matchedProduct?.productName || "",
          isassigned: ele.isassigned?.toString(),
          createddate: getDisplayDate(ele.createddate, "DD-MM-YYYY"),
          status: ele.status === "Active" ? "Un Scan" : "Scan",
        };
      });
    } catch (error) {
      console.error("fetchAllQRByTemplate error:", error);
      return [];
    } finally {
      setLoader(false);
    }
  };

  const getDownloadFileName = (suffix, templateCode) => {
    const product = templates.find((p) => p.code === templateCode);
    const name = (
      product
        ? [product.productName, product?.others?.subCategory]
            .filter(Boolean)
            .join("_")
        : "QR_Code"
    )
      .replace(/\s+/g, " ")
      .trim()
      .replace(/[\\/:*?"<>|]/g, "_");
    return `${name}_${suffix}`;
  };

  const handleDownloadCSV = async (templateCode) => {
    const allData = await fetchAllQRByTemplate(templateCode);
    if (!allData.length) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("QR Code Data");

      const headers = [
        "QR Image",
        "QR Code",
        "Template Code",
        "Product Name",
        "Is Assign",
        "Created Date",
        "Status",
      ];

      worksheet.columns = headers.map((key) => ({
        header: key,
        key,
        width: key === "QR Image" ? 20 : 30,
      }));

      allData.forEach((row, index) => {
        const rowIndex = index + 2;

        worksheet.addRow({
          "QR Code": row.code ?? "",
          "Template Code": row.templatecode ?? "",
          "Product Name": row.productName ?? "",
          "Is Assign": row.isassigned ? "Yes" : "No",
          // createddate is already formatted (DD-MM-YYYY) in fetchAllQRByTemplate
          "Created Date": row.createddate || "",
          Status: row.status,
        });

        worksheet.getRow(rowIndex).height = 90;

        if (row.qr && row.qr.startsWith("data:image")) {
          const imageId = workbook.addImage({
            base64: row.qr,
            extension: "png",
          });

          worksheet.addImage(imageId, {
            tl: { col: 0, row: rowIndex - 1 },
            ext: { width: 90, height: 90 },
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, getDownloadFileName("QR_Code_Data.xlsx", templateCode));
    } catch (error) {
      console.error("Error generating Excel:", error);
    }
  };

  return (
    <>
      <Header name={"QR Codes"} />
      {loader && (
        <div className="mainPreloaderMain">
          <div className="mainPreloader">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      )}
      <div className="dashboard__content">
        <div className="container-fluid">
          <div className="dashboard__transactionsCard">
            <div className="dashboard__header">
              <div className="dashboard__left">
                <h4 id="transction_list">All QR Code List</h4>
              </div>
            </div>
            <div className="dashboard__body">
              <Paginated
                data={filteredData}
                columns={COLUMNS}
                onSearch={searchQRByCode}
                showTable={hasSearched}
                headerLeft={
                  !hasSearched && (
                    <div className="customTable__showingRecords">
                      <div className="form-group">
                        <label>Show Records:</label>
                        <select
                          className="form-control selectArrow"
                          value={templatePageSize}
                          onChange={(e) =>
                            setTemplatePageSize(Number(e.target.value))
                          }
                        >
                          {[10, 25, 50].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )
                }
              >
                <TemplateTable
                  data={templateList}
                  onDownload={handleDownloadCSV}
                  pageSize={templatePageSize}
                />
              </Paginated>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QRCodeList;
